import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, signup, loginWithGoogle, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = loc.state?.returnTo || "/account";

  useEffect(() => {
    if (user) nav(returnTo, { replace: true });
  }, [user, nav, returnTo]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(email.trim(), password, name.trim());
        toast.success(`Welcome, ${name.split(" ")[0]}.`);
      } else {
        await login(email.trim(), password);
        toast.success("Welcome back.");
      }
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const isSignup = mode === "signup";

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Editorial column */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-6"
        >
          <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--sr-accent)]" />
            {isSignup ? "Create an account" : "Return to your rituals"}
          </p>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]">
            {isSignup ? (
              <>
                Slower letters.
                <br />
                <em className="italic font-light text-[color:var(--sr-accent)]">
                  Softer mornings.
                </em>
              </>
            ) : (
              <>
                Welcome
                <br />
                <em className="italic font-light text-[color:var(--sr-accent)]">
                  back home.
                </em>
              </>
            )}
          </h1>
          <p className="mt-8 text-[color:var(--sr-text)]/75 leading-relaxed max-w-md">
            {isSignup
              ? "One quiet account for saved bags, faster checkout, and a private thread of every bottle you've collected."
              : "Sign in to see your orders, saved bags, and any pickup you've reserved at a farmers market."}
          </p>
        </motion.div>

        {/* Form column */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-5 lg:col-start-8 border hairline p-8 md:p-12 bg-[color:var(--sr-secondary)]"
        >
          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 px-6 py-4 border hairline hover:border-[color:var(--sr-primary)] transition-colors bg-[color:var(--sr-bg)]"
          >
            <GoogleGlyph />
            <span className="label-eyebrow text-[color:var(--sr-primary)]">
              Continue with Google
            </span>
          </button>

          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-[color:var(--sr-border)]" />
            <span className="label-eyebrow text-[color:var(--sr-muted)]">or</span>
            <div className="flex-1 h-px bg-[color:var(--sr-border)]" />
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {isSignup && (
              <Field
                label="Your name"
                testid="auth-name"
                value={name}
                onChange={setName}
                required
                autoComplete="name"
              />
            )}
            <Field
              label="Email"
              testid="auth-email"
              type="email"
              value={email}
              onChange={setEmail}
              required
              autoComplete="email"
            />
            <Field
              label="Password"
              testid="auth-password"
              type="password"
              value={password}
              onChange={setPassword}
              required
              minLength={isSignup ? 8 : undefined}
              autoComplete={isSignup ? "new-password" : "current-password"}
            />

            <button
              type="submit"
              disabled={busy}
              data-testid={isSignup ? "signup-submit" : "login-submit"}
              className="w-full inline-flex items-center justify-between px-6 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
            >
              <span>
                {busy ? "…" : isSignup ? "Create account" : "Sign in"}
              </span>
              <ArrowRight size={16} strokeWidth={1.25} />
            </button>
          </form>

          <p className="mt-8 text-xs text-[color:var(--sr-muted)] text-center">
            {isSignup ? "Already with us?" : "New to Smiling Roots?"}{" "}
            <button
              onClick={() => setMode(isSignup ? "login" : "signup")}
              type="button"
              data-testid="toggle-auth-mode"
              className="text-[color:var(--sr-primary)] underline decoration-[color:var(--sr-accent)] underline-offset-4"
            >
              {isSignup ? "Sign in" : "Create an account"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required, autoComplete, minLength }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">
        {label}
      </span>
      <input
        type={type}
        value={value}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border-0 border-b hairline focus:border-[color:var(--sr-primary)] outline-none py-2 text-[color:var(--sr-primary)] text-lg font-serif"
      />
    </label>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
